import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getFileExtension from "@insite/client-framework/Common/Utilities/getFileExtension";
import siteMessage from "@insite/client-framework/SiteMessage";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import loadShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTos";
import {
    getCurrentShipTosDataView,
    getShipTosDataView,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import FileUpload, { FileUploadPresentationProps } from "@insite/mobius/FileUpload";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { trackTaxCertificateUploadEvent } from "@waytek/Handlers/Utilities/CustomTracking";
import setTaxFields from "@waytek/Store/Components/TaxFields/SetTaxFields";
import { CustomState } from "@waytek/Store/Reducers";
import { TaxCertificateType } from "@waytek/Store/State/TaxFields/TaxFieldsState";
import { colorStrings } from "@waytek/Utilities/variables";
import React, { useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    state?: string;
    extendedStyles?: TaxFieldsStyles;
}

interface TaxFieldsStyles {
    fileUploader?: FileUploadPresentationProps;
    taxIdGridItem?: GridItemProps;
    taxIdText?: TextFieldProps;
    taxCertificateGridItem?: GridItemProps;
    taxIdSelect?: SelectPresentationProps;
    tooltip?: TooltipPresentationProps;
}

const taxFieldStyles: TaxFieldsStyles = {
    fileUploader: {
        buttonProps: {
            variant: "tertiary",
        },
    },
    taxIdGridItem: {
        width: 6,
        css: css`
            flex-direction: column;
        `,
    },
    taxIdText: {
        label: "Tax ID",
    },
    taxCertificateGridItem: {
        css: css`
            flex-direction: column;

            div[class^="UploadWrapper--"] {
                min-width: 100%;
            }

            input.mobiusFileVisual {
                padding: 0 55px 0 5px !important;
                text-overflow: ellipsis;
            }

            label {
                display: block;
                margin-bottom: 8px;
            }
            input[type="file"] {
                width: 100%;
                padding: 8px;
                border-radius: 4px;
            }
            .error {
                color: ${getColor(colorStrings.dangerMain)};
                margin-top: 4px;
                font-size: 12px;
            }
        `,
    },
    taxIdSelect: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
};

const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    const settings = getSettingsCollection(state);
    const pageType = getCurrentPage(state).type;

    let currentShipToState = getCurrentShipTosDataView(state).value;
    let shipTosDataView;

    if (pageType === "CheckoutShippingPage") {
        const { cartId } = state.pages.checkoutShipping;
        const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;
        shipTosDataView = cart
            ? getShipTosDataView(state, { billToId: cart.billToId, expand: ["validation"], exclude: ["showAll"] })
            : undefined;
        currentShipToState = shipTosDataView?.value;
    }

    // Remove duplicates using Set
    const taxIdOptions =
        currentShipToState && ownProps.state
            ? [
                  ...new Set(
                      currentShipToState
                          // First filter valid shipTos with matching state
                          .filter(shipTo => shipTo.state?.id && shipTo.state.id === ownProps.state)
                          // Then map to get the taxCertificateId values
                          .map(state => state.properties?.taxCertificateId?.trim())
                          // Finally filter out any empty/null values
                          .filter(id => id && id !== ""),
                  ),
              ]
            : [];

    return {
        taxID: state.taxFields.taxID,
        taxCertificate: state.taxFields.taxCertificate as TaxCertificateType | null,
        sendNewShipTosToERP: (settings.customSettings as { sendNewShipTosToERP: boolean }).sendNewShipTosToERP === true,
        taxIdOptions,
        shouldLoadShipTos: !getCurrentShipTosDataView(state).value,
        shouldShowTaxOptions: pageType !== "CreateAccountPage",
    };
};

const mapDispatchToProps = {
    setTaxFields,
    loadShipTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const TaxFields: React.FC<Props> = ({
    taxID,
    taxCertificate,
    extendedStyles,
    sendNewShipTosToERP,
    setTaxFields,
    taxIdOptions,
    state,
    shouldLoadShipTos,
    shouldShowTaxOptions,
}) => {
    const [taxIdError, setTaxIdError] = useState<string | undefined>();
    const [taxCertificateError, setTaxCertificateError] = useState<string | undefined>();

    const [styles] = useState(() => mergeToNew(taxFieldStyles, extendedStyles));

    const fileUploadRef = useRef({ value: "" } as HTMLInputElement);

    const [incorrectFileExtension, setIncorrectFileExtension] = React.useState(false);

    if (!sendNewShipTosToERP) {
        return null;
    }

    useEffect(() => {
        return () => {
            setTaxFields({
                taxID: "",
                taxCertificate: {
                    name: "",
                    type: "",
                    base64: "",
                },
            });
        };
    }, []);

    useEffect(() => {
        if (shouldLoadShipTos) {
            // Add cache-busting parameter
            loadShipTos({
                expand: ["approvals"],
            });
        }
    }, [shouldLoadShipTos, loadShipTos]);

    useEffect(() => {
        if (state) {
            setTaxFields({
                taxID: "",
                taxCertificate: {
                    name: "",
                    type: "",
                    base64: "",
                },
            });
        }
    }, [state]);

    const onChangeTaxId = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const value = event.target.value;
        setTaxFields({
            taxID: value,
            taxCertificate: {
                name: "",
                type: "",
                base64: "",
            },
        });
    };

    const fileChangeHandler = (event: React.ChangeEvent<any>) => {
        const uploadedFile = event.target.files[0];

        if (uploadedFile) {
            const fileExtension = getFileExtension(uploadedFile.name);
            const incorrectFileExtension = ["pdf"].indexOf(fileExtension) === -1;
            setIncorrectFileExtension(incorrectFileExtension);
            setIsBadFile({ isBadFile: incorrectFileExtension });
            setUploadLimitExceeded({ uploadLimitExceeded: false });

            const reader = new FileReader();
            reader.onload = () => {
                const base64String = reader.result as string;
                setTaxFields({
                    taxID,
                    taxCertificate: {
                        name: uploadedFile.name,
                        type: uploadedFile.type,
                        base64: base64String, // for the api for BE we will send only this base64 string
                    },
                });
            };
            !incorrectFileExtension && trackTaxCertificateUploadEvent();
            reader.readAsDataURL(uploadedFile);
        }
    };

    return (
        <>
            {shouldShowTaxOptions && (
                <GridItem {...styles.taxIdGridItem}>
                    <Select
                        {...styles.taxIdSelect}
                        name="taxId"
                        value={taxID}
                        onChange={onChangeTaxId}
                        error={taxIdError}
                        data-test-selector="customerAddressForm_taxId"
                        label={translate("ISC_Tax_ID")}
                        disabled={taxIdOptions.length === 0}
                    >
                        <option value="">Select Tax ID</option>
                        {taxIdOptions.map(option => (
                            <option key={option?.toString()} value={option?.toString() || ""}>
                                {option}
                            </option>
                        ))}
                    </Select>
                </GridItem>
            )}

            <GridItem {...styles.taxCertificateGridItem} width={shouldShowTaxOptions ? 6 : 12}>
                <FileUpload
                    {...styles.fileUploader}
                    inputRef={fileUploadRef}
                    fileName={taxCertificate?.name || ""}
                    accept=".pdf"
                    label={
                        <>
                            <Typography>{translate("ISC_Tax_File")}</Typography>{" "}
                            <Tooltip {...styles.tooltip} text={siteMessage("ISC_Tax_File_TooltipMessage").toString()} />
                        </>
                    }
                    labelPosition="top"
                    error={incorrectFileExtension ? translate("must be .pdf") : undefined}
                    onFileChange={fileChangeHandler}
                    uid="orderUploadTaxFileUpload"
                    hideButton={true}
                    disabled={!!taxID}
                />
                {taxCertificateError && <div className="error">{taxCertificateError}</div>}
            </GridItem>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(TaxFields);
